import React from 'react'
import { Box, Grid, MenuItem, TextField, Typography, FormControlLabel, Checkbox } from '@material-ui/core'
import { useFormik } from 'formik'
import SelectComponent from 'UI/Select'
import Button from 'UI/Button/ButtonWithPreloader'
import TipTapEditor from 'UI/TipTapEditor'
import { IArticleForm, ISection } from 'typescript/interfaces/knowledgeBase'
import { uploadArticleImage } from 'services/knowledgeBase'
import { knowledgeBaseFormSchema } from './schema'

interface Props {
  heading: string
  sections: Array<ISection>
  initValues: IArticleForm
  loading?: boolean
  onSubmit: (values: IArticleForm) => void
  onCancel: () => void
}

const defaultInitValues: IArticleForm = {
  title: '',
  sectionId: '',
  bodyHtml: '',
  isPinned: false,
}

const KnowledgeBaseForm = ({ heading, sections, initValues = defaultInitValues, loading, onSubmit, onCancel }: Props) => {
  const { handleSubmit, values, errors, touched, handleChange, setFieldValue } = useFormik({
    initialValues: initValues,
    enableReinitialize: true,
    validationSchema: knowledgeBaseFormSchema,
    onSubmit,
  })

  return (
    <Box component="form" onSubmit={(e: React.FormEvent) => handleSubmit(e)}>
      <Typography variant="h2" color="textSecondary">
        {heading}
      </Typography>
      <Box mt="3rem">
        <Grid container spacing={8}>
          <Grid item xs={12} md={6}>
            <TextField
              name="title"
              variant="outlined"
              label="Name"
              fullWidth
              value={values.title}
              onChange={handleChange}
              error={Boolean(touched.title && errors.title)}
              helperText={touched.title && errors.title}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <SelectComponent
              label="Section"
              name="sectionId"
              value={values.sectionId}
              onChange={handleChange}
              error={Boolean(touched.sectionId && errors.sectionId)}
              helperText={touched.sectionId && (errors.sectionId as string)}
            >
              {sections.map((section) => (
                <MenuItem key={section.id} value={section.id}>
                  {section.name}
                </MenuItem>
              ))}
            </SelectComponent>
          </Grid>
          <Grid item xs={12}>
            <TipTapEditor
              value={values.bodyHtml}
              onChange={(html) => setFieldValue('bodyHtml', html)}
              onUploadImage={uploadArticleImage}
              error={Boolean(touched.bodyHtml && errors.bodyHtml)}
              helperText={touched.bodyHtml && (errors.bodyHtml as string)}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Checkbox color="primary" checked={values.isPinned} onChange={(e) => setFieldValue('isPinned', e.target.checked)} />}
              label="Pin to the main Knowledge Base page"
            />
          </Grid>
          <Grid item xs={12}>
            <Box display="flex" alignItems="center">
              <Button loading={loading} type="submit" variant="contained" color="primary">
                Publish
              </Button>
              <Box ml="1.5rem">
                <Button type="button" variant="text" color="primary" onClick={onCancel}>
                  Cancel
                </Button>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  )
}

export default KnowledgeBaseForm
